import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MARKET_REFRESH_MS, playersAPI, tradesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Toast from '../components/Toast';
import MarketLockBanner from '../components/MarketLockBanner';
import { PlayerPhoto } from '../components/PlayerCard';
import useMarketStatus from '../hooks/useMarketStatus';
import { TrendingUp, TrendingDown, Clock, Lock } from 'lucide-react';

export default function PortfolioPage() {
  const [portfolio, setPortfolio] = useState([]);
  const [history, setHistory]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [toast, setToast]         = useState(null);
  const [tab, setTab]             = useState('holdings');
  const { user, updateAfterTrade } = useAuth();
  const navigate                  = useNavigate();
  const { status: marketStatus }  = useMarketStatus();

  const marketLocked = marketStatus?.isLive || !marketStatus?.tradingWindowOpen;
  const tradesRemaining = user?.trades_remaining
    ?? (user ? (user.max_trades || 100) - (user.trade_count || 0) : undefined);

  const fetchData = useCallback(async () => {
    try {
      const [portRes, histRes] = await Promise.all([
        playersAPI.getPortfolio(),
        tradesAPI.history(),
      ]);
      setPortfolio(portRes.data);
      setHistory(histRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, MARKET_REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleSell = async (player_id) => {
    if (marketLocked) {
      setToast({ msg: marketStatus?.isLive ? 'Trading paused during live match' : 'Trading window closed (3 PM–midnight IST)', type: 'error' });
      return;
    }
    try {
      const r = await tradesAPI.sell(player_id);
      updateAfterTrade(r.data);
      setToast({ msg: r.data.message, type: 'success' });
      fetchData();
    } catch (err) {
      setToast({ msg: err.response?.data?.error || 'Sell failed', type: 'error' });
    }
  };

  const portfolioValue = portfolio.reduce((s, h) => s + parseFloat(h.current_stock_price), 0);
  const cashPoints     = parseFloat(user?.points || 0);
  const totalValue     = portfolioValue + cashPoints;
  const totalPnl       = portfolio.reduce((s, h) => s + parseFloat(h.pnl || 0), 0);
  const totalReturn    = ((totalValue - 1000) / 1000) * 100;

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onDismiss={() => setToast(null)} />}

      <div className="page-header">
        <h1 className="page-title">My Portfolio</h1>
        <p className="page-subtitle">Track your holdings and performance</p>
      </div>

      <MarketLockBanner status={marketStatus} tradesRemaining={tradesRemaining} />

      {/* Metrics */}
      <div className="metrics-row">
        <div className="metric-card">
          <div className="metric-label">Total Value</div>
          <div className="metric-value accent">{totalValue.toFixed(1)} pts</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Cash</div>
          <div className="metric-value">{cashPoints.toFixed(1)} pts</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Invested</div>
          <div className="metric-value">{portfolioValue.toFixed(1)} pts</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Return</div>
          <div className={`metric-value ${totalReturn >= 0 ? 'green' : 'red'}`}>
            {totalReturn >= 0 ? '+' : ''}{totalReturn.toFixed(2)}%
          </div>
        </div>
      </div>

      {/* Trades remaining meter */}
      {tradesRemaining !== undefined && (
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)', padding: '10px 14px',
          marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Season trade budget</span>
              <span style={{
                fontSize: '0.75rem', fontWeight: 600,
                color: tradesRemaining === 0 ? 'var(--red)' : tradesRemaining <= 10 ? 'var(--accent)' : 'var(--text-primary)',
              }}>
                {tradesRemaining} / {user?.max_trades || 100} remaining
              </span>
            </div>
            <div style={{ height: 4, background: 'var(--bg-secondary)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${Math.max(0, (tradesRemaining / (user?.max_trades || 100)) * 100)}%`,
                background: tradesRemaining === 0 ? 'var(--red)' : tradesRemaining <= 10 ? 'var(--accent)' : 'var(--green)',
                borderRadius: 2,
                transition: 'width 0.3s',
              }} />
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: '1.25rem' }}>
        <button className={`filter-chip${tab === 'holdings' ? ' active' : ''}`} onClick={() => setTab('holdings')}>
          Holdings ({portfolio.length})
        </button>
        <button className={`filter-chip${tab === 'history' ? ' active' : ''}`} onClick={() => setTab('history')}>
          <Clock size={12} style={{ display: 'inline', marginRight: 4 }} />
          History ({history.length})
        </button>
      </div>

      {/* Holdings tab */}
      {tab === 'holdings' && (
        <>
          {portfolio.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
              <p>No holdings yet.</p>
              <button className="btn btn-outline" style={{ marginTop: 12 }} onClick={() => navigate('/')}>
                Browse Market
              </button>
            </div>
          ) : (
            portfolio.map(h => {
              const pnl    = parseFloat(h.pnl || 0);
              const pnlPct = parseFloat(h.pnl_pct || 0);
              const tc     = h.team_color || '#3b82f6';
              return (
                <div
                  key={h.id}
                  className="portfolio-item"
                  onClick={() => navigate(`/player/${h.player_id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <PlayerPhoto player={h} size={40} />

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {h.name}
                      {h.is_playing_today && <span className="live-badge"><span className="live-dot" /> LIVE</span>}
                    </div>
                    <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                      <span style={{ color: tc, fontWeight: 600 }}>{h.team_short}</span>
                      &nbsp;· Bought @ {parseFloat(h.buy_price).toFixed(1)} pts
                    </div>
                  </div>

                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.1rem' }}>
                      {parseFloat(h.current_stock_price).toFixed(1)}
                    </div>
                    <div style={{ fontSize: '0.74rem', color: pnl >= 0 ? 'var(--green)' : 'var(--red)', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3 }}>
                      {pnl >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                      {pnl >= 0 ? '+' : ''}{pnl.toFixed(1)} ({pnlPct.toFixed(1)}%)
                    </div>
                  </div>

                  <button
                    className="btn btn-sell"
                    style={{ padding: '7px 14px', fontSize: '0.8rem', flexShrink: 0, opacity: marketLocked ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 4 }}
                    onClick={e => { e.stopPropagation(); handleSell(h.player_id); }}
                    disabled={marketLocked}
                    title={marketLocked ? (marketStatus?.isLive ? 'Live match' : 'Window closed 3PM–midnight') : 'Sell'}
                  >
                    {marketLocked && <Lock size={11} />}
                    {marketLocked ? 'Locked' : 'Sell'}
                  </button>
                </div>
              );
            })
          )}

          {portfolio.length > 0 && (
            <div style={{ marginTop: '1rem', padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', fontSize: '0.78rem', color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between' }}>
              <span>Unrealised P&L</span>
              <span style={{ fontWeight: 600, color: totalPnl >= 0 ? 'var(--green)' : 'var(--red)' }}>
                {totalPnl >= 0 ? '+' : ''}{totalPnl.toFixed(1)} pts
              </span>
            </div>
          )}
        </>
      )}

      {/* History tab */}
      {tab === 'history' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {history.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No transactions yet</div>
          ) : (
            history.map(tx => (
              <div key={tx.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 1.25rem', borderBottom: '1px solid var(--border)' }}>
                <span style={{
                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', fontWeight: 700,
                  background: tx.type === 'buy' ? 'var(--green-dim)' : 'var(--red-dim)',
                  color: tx.type === 'buy' ? 'var(--green)' : 'var(--red)',
                }}>
                  {tx.type === 'buy' ? '↓' : '↑'}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, fontSize: '0.87rem' }}>{tx.player_name}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                    {tx.team_short} · {new Date(tx.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, color: tx.type === 'buy' ? 'var(--red)' : 'var(--green)' }}>
                    {tx.type === 'buy' ? '-' : '+'}{Math.abs(parseFloat(tx.price)).toFixed(1)} pts
                  </div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{tx.type}</div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
